// CxReputation — iter180i
// "Reputation Management" tab inside Customer Pulse Index.
// Three sub-zones:
//   1. Connection card  (Connect/Disconnect Google Business Profile)
//   2. Location Mapping (pair each RestroPulse outlet with a GBP location)
//   3. Reputation Dashboard (KPIs · rating distribution · per-outlet · recent reviews)
//
// All data goes through the GBP review-sync engine. Manual "Sync now"
// triggers an on-demand fetch in addition to the 3-hour APScheduler cron.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  Link2, Unlink, AlertCircle, CheckCircle2, RefreshCw, ShieldCheck,
  Loader2, Star, MessageSquare, MapPin, Building2, ExternalLink,
  Stethoscope, ChevronDown, ChevronRight,
} from 'lucide-react';
import { fmtDateIST } from '../../lib/tz';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function GoogleBadge({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09a6.61 6.61 0 0 1 0-4.19V7.07H2.18a11 11 0 0 0 0 9.87l3.66-2.85z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function Stars({ rating, size = 12 }) {
  const r = Math.round(Number(rating) || 0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= r ? 'fill-[#F5B100] text-[#F5B100]' : 'fill-none text-[#E2E8DF]'}
        />
      ))}
    </div>
  );
}

export default function CxReputation({ headers }) {
  const { getHeaders } = useAuth();
  const authHeaders = useMemo(() => headers || getHeaders(), [headers, getHeaders]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [conn, setConn] = useState({ connections: [], configured: false });
  const [outlets, setOutlets] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [reviewsResp, setReviewsResp] = useState({ items: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, m, s, r] = await Promise.all([
        axios.get(`${API}/google/connections`, { headers: authHeaders }),
        axios.get(`${API}/admin/outlets`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/google/location-mappings`, { headers: authHeaders }),
        axios.get(`${API}/google/reputation/summary`, { headers: authHeaders }),
        axios.get(`${API}/google/reviews?limit=20`, { headers: authHeaders }),
      ]);
      setConn(c.data);
      setOutlets(Array.isArray(o.data) ? o.data : []);
      setMappings(m.data.mappings || []);
      setSummary(s.data);
      setReviewsResp(r.data);
    } catch (e) {
      toast.error('Failed to load reputation data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // OAuth callback bounce-back
  useEffect(() => {
    const status = searchParams.get('google');
    const detail = searchParams.get('detail');
    if (!status) return;
    if (status === 'connected') {
      toast.success('Google Business Profile connected');
      loadAll();
    } else if (status === 'denied') {
      toast.error(`Connection cancelled${detail ? `: ${detail}` : ''}`);
    }
    searchParams.delete('google');
    searchParams.delete('detail');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, loadAll]);

  // ── Connect: full-page navigation with JWT in ?t= (Authorization header
  //   doesn't survive a redirect to Google). The backend accepts both header
  //   and ?t= for /oauth/google/start specifically.
  const connect = () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please log in again'); return; }
    window.location.href = `${API}/oauth/google/start?t=${encodeURIComponent(token)}`;
  };

  const disconnect = async (google_user_id, email) => {
    if (!window.confirm(`Disconnect ${email}? Synced reviews and location mappings will be removed.`)) return;
    setBusy(`dc-${google_user_id}`);
    try {
      await axios.delete(`${API}/google/connections/${encodeURIComponent(google_user_id)}`,
        { headers: authHeaders });
      toast.success('Disconnected');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    } finally { setBusy(null); }
  };

  const syncNow = async () => {
    setBusy('sync');
    try {
      const r = await axios.post(`${API}/google/reviews/sync`, {}, { headers: authHeaders });
      const { locations_synced, reviews_upserted, errors } = r.data;
      if (errors && errors.length > 0) {
        const detail = errors[0]?.detail || 'unknown';
        if (detail.startsWith('quota_pending')) {
          toast.warning('Google review API quota not yet granted. Apply at developers.google.com/my-business.');
        } else {
          toast.warning(`Synced ${locations_synced} locations, ${reviews_upserted} reviews. ${errors.length} errors.`);
        }
      } else {
        toast.success(`Synced ${reviews_upserted} review(s) from ${locations_synced} location(s)`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="cx-reputation-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#9CA3A0]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cx-reputation-root">
      {/* iter180j — pending Google approval banner. Removed once GBP API quota
          is granted and the integration goes live. */}
      <div
        className="p-3.5 rounded-md border border-[#B8D4E8] bg-[#EAF3FB]"
        data-testid="gbp-pending-banner"
      >
        <div className="flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-[#1E4D7B] mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-[#1E4D7B] leading-relaxed">
            <div className="font-semibold mb-0.5">
              Google Business Profile integration is configured and awaiting Google API access approval.
            </div>
            <div className="text-[#3A5B7D]">
              OAuth, consent screen, scope handling, location mapping framework, and review-sync engine
              are fully built. Reviews will start flowing automatically once Google grants GBP API quota
              to the OAuth project. No further admin action required in the meantime.
            </div>
          </div>
        </div>
      </div>

      <ConnectionCard
        conn={conn}
        onConnect={connect}
        onDisconnect={disconnect}
        busy={busy}
      />

      {conn.connections.length > 0 && (
        <LocationMappingCard
          conn={conn}
          outlets={outlets}
          mappings={mappings}
          authHeaders={authHeaders}
          onChanged={loadAll}
          onSyncNow={syncNow}
          syncing={busy === 'sync'}
        />
      )}

      <ReputationDashboardCard
        summary={summary}
        outlets={outlets}
        reviews={reviewsResp.items}
        totalReviews={reviewsResp.total}
        onSyncNow={syncNow}
        syncing={busy === 'sync'}
        hasMappings={mappings.length > 0}
      />
    </div>
  );
}

/* ──────────────────────── 1. Connection Card ──────────────────────── */
function ConnectionCard({ conn, onConnect, onDisconnect, busy }) {
  if (!conn.configured) {
    return (
      <Card className="border-[#E2E8DF] shadow-sm" data-testid="gbp-not-configured">
        <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <GoogleBadge /> Google Business Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-5">
          <div className="flex items-start gap-2 p-3 rounded-md border border-[#FFD8A8] bg-[#FFF6E5]">
            <AlertCircle className="w-4 h-4 text-[#D89B5B] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#5C4528]">
              <div className="font-semibold">Not configured for this deployment</div>
              <div>Ask the platform owner to set <code className="font-mono text-[11.5px] bg-white px-1 rounded">GOOGLE_OAUTH_CLIENT_ID</code> and <code className="font-mono text-[11.5px] bg-white px-1 rounded">GOOGLE_OAUTH_CLIENT_SECRET</code> in environment.</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E2E8DF] shadow-sm" data-testid="gbp-connection-card">
      <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <GoogleBadge /> Google Business Profile
          </CardTitle>
          <span className="text-[10px] uppercase tracking-wider text-[#9CA3A0]">
            Reviews & ratings · feeds CPI
          </span>
        </div>
      </CardHeader>
      <CardContent className="px-4 py-5 space-y-3">
        {conn.connections.length === 0 ? (
          <div>
            <p className="text-[13px] text-[#3A4541] mb-3 leading-relaxed">
              Connect your Google account to automatically pull Google reviews
              for every property you manage. Reviews flow into the same
              Customer Pulse Index dashboard alongside QR-form feedback.
            </p>
            <Button
              onClick={onConnect}
              className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-9 text-[13px]"
              data-testid="gbp-connect-btn"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect Google Business Profile
            </Button>
            <p className="text-[10.5px] text-[#9CA3A0] mt-2.5 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              RestroPulse adheres to Google's Limited Use policy.
              <a href="/legal/privacy" className="text-[color:var(--color-primary)] underline ml-1" target="_blank" rel="noopener noreferrer">Privacy</a>
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {conn.connections.map((c) => (
              <div
                key={c.google_user_id}
                className="flex items-center gap-3 p-3 rounded-md border border-[#E2E8DF] bg-white"
                data-testid={`gbp-conn-${c.google_user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#EEF4EF] flex items-center justify-center shrink-0">
                  {c.needs_reauth
                    ? <AlertCircle className="w-4 h-4 text-[#D89B5B]" />
                    : <CheckCircle2 className="w-4 h-4 text-[color:var(--color-primary)]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A2421] truncate">{c.email}</div>
                  <div className="text-[11px] text-[#5C6B64]">
                    {c.needs_reauth
                      ? 'Re-authorization needed — please reconnect.'
                      : `Connected ${c.created_at ? fmtDateIST(c.created_at) : ''}`}
                  </div>
                </div>
                {c.needs_reauth ? (
                  <Button
                    onClick={onConnect}
                    size="sm"
                    className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-8 text-[12px]"
                    data-testid={`gbp-reconnect-${c.google_user_id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reconnect
                  </Button>
                ) : (
                  <Button
                    onClick={() => onDisconnect(c.google_user_id, c.email)}
                    disabled={busy === `dc-${c.google_user_id}`}
                    variant="outline"
                    size="sm"
                    className="h-8 text-[12px] text-[#A33A3A] border-[#E6CFCF] hover:bg-[#FDF4F4]"
                    data-testid={`gbp-disconnect-${c.google_user_id}`}
                  >
                    {busy === `dc-${c.google_user_id}`
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect</>}
                  </Button>
                )}
              </div>
            ))}
            <Button
              onClick={onConnect}
              variant="outline"
              size="sm"
              className="w-full h-8 text-[12px] text-[color:var(--color-primary)] border-[#E2E8DF]"
              data-testid="gbp-connect-another"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect another Google account
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────── 2. Location Mapping Card ──────────────────── */
function LocationMappingCard({ conn, outlets, mappings, authHeaders, onChanged, onSyncNow, syncing }) {
  // For each connection, lazily load its locations once expanded.
  const [openConn, setOpenConn] = useState(conn.connections[0]?.google_user_id || null);
  const [locationsByConn, setLocationsByConn] = useState({});
  const [locationsError, setLocationsError] = useState({});
  const [loadingConn, setLoadingConn] = useState(null);
  const [mappingBusy, setMappingBusy] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const runDiagnostics = useCallback(async () => {
    if (!openConn) return;
    setDiagBusy(true);
    try {
      const r = await axios.get(
        `${API}/google/diagnostics?google_user_id=${encodeURIComponent(openConn)}`,
        { headers: authHeaders },
      );
      setDiagnostics(r.data);
      setDiagOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostics failed');
    } finally { setDiagBusy(false); }
  }, [openConn, authHeaders]);

  const mappingByOutlet = useMemo(
    () => Object.fromEntries(mappings.map((m) => [m.outlet_id, m])),
    [mappings],
  );

  const fetchLocations = useCallback(async (google_user_id) => {
    if (locationsByConn[google_user_id]) return;
    setLoadingConn(google_user_id);
    try {
      const r = await axios.get(
        `${API}/google/locations?google_user_id=${encodeURIComponent(google_user_id)}`,
        { headers: authHeaders },
      );
      setLocationsByConn((prev) => ({ ...prev, [google_user_id]: r.data.locations || [] }));
      setLocationsError((prev) => ({ ...prev, [google_user_id]: null }));
    } catch (e) {
      // Pass through the detail (may be a dict containing google_error +
      // hint + the original Google response body). The diagnostics panel
      // below renders this verbatim.
      const detail = e?.response?.data?.detail;
      setLocationsError((prev) => ({
        ...prev,
        [google_user_id]: {
          status: e?.response?.status,
          detail,
        },
      }));
      setLocationsByConn((prev) => ({ ...prev, [google_user_id]: [] }));
    } finally { setLoadingConn(null); }
  }, [authHeaders, locationsByConn]);

  useEffect(() => {
    if (openConn) fetchLocations(openConn);
  }, [openConn, fetchLocations]);

  const assignMapping = async (outlet, locOrUnset) => {
    setMappingBusy(outlet.id);
    try {
      if (locOrUnset === '__unset__') {
        await axios.delete(`${API}/google/location-mappings/${encodeURIComponent(outlet.id)}`,
          { headers: authHeaders });
        toast.success(`Unmapped ${outlet.name}`);
      } else {
        const [google_user_id, location_name] = locOrUnset.split('::');
        const loc = (locationsByConn[google_user_id] || []).find((l) => l.name === location_name);
        if (!loc) { toast.error('Location not found'); return; }
        await axios.post(
          `${API}/google/location-mappings`,
          {
            outlet_id: outlet.id,
            google_user_id,
            google_account_name: loc.google_account_name,
            google_location_name: loc.name,
            title: loc.title,
            place_id: loc.place_id,
          },
          { headers: authHeaders },
        );
        toast.success(`Mapped ${outlet.name} → ${loc.title}`);
      }
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mapping failed');
    } finally { setMappingBusy(null); }
  };

  // Flatten available locations from every connection for the dropdown.
  const availableOptions = useMemo(() => {
    const opts = [];
    for (const c of conn.connections) {
      const locs = locationsByConn[c.google_user_id] || [];
      for (const l of locs) {
        opts.push({
          value: `${c.google_user_id}::${l.name}`,
          label: `${l.title || l.name}${l.store_code ? ` (${l.store_code})` : ''}`,
          account: c.email,
        });
      }
    }
    return opts;
  }, [conn, locationsByConn]);

  return (
    <Card className="border-[#E2E8DF] shadow-sm" data-testid="gbp-mapping-card">
      <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[color:var(--color-primary)]" /> Map outlets to Google locations
          </CardTitle>
          <Button
            onClick={onSyncNow}
            disabled={syncing}
            size="sm"
            className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-8 text-[12px]"
            data-testid="gbp-sync-now-btn"
          >
            {syncing
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing…</>
              : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync now</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 py-4">
        {/* Connection selector — only matters for multi-property groups using
            different Google accounts per property. */}
        {conn.connections.length > 1 && (
          <div className="mb-4 flex items-center gap-2 text-[12px]">
            <span className="text-[#5C6B64]">Pick account:</span>
            <Select value={openConn || ''} onValueChange={setOpenConn}>
              <SelectTrigger className="h-8 w-[280px] text-[12px]" data-testid="gbp-conn-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {conn.connections.map((c) => (
                  <SelectItem key={c.google_user_id} value={c.google_user_id}>{c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loadingConn === openConn && (
          <div className="py-6 flex items-center justify-center text-[12px] text-[#9CA3A0]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Fetching Google locations…
          </div>
        )}

        {outlets.length === 0 ? (
          <div className="text-[12.5px] text-[#5C6B64] py-4">
            Add outlets in <b>Organization → Outlets</b> first, then return here to map them to Google locations.
          </div>
        ) : availableOptions.length === 0 && loadingConn !== openConn ? (
          <LocationsErrorPanel
            error={locationsError[openConn]}
            diagnostics={diagnostics}
            diagBusy={diagBusy}
            diagOpen={diagOpen}
            setDiagOpen={setDiagOpen}
            onRunDiagnostics={runDiagnostics}
            onRetry={() => { setLocationsByConn((p) => { const n={...p}; delete n[openConn]; return n; }); }}
          />
        ) : (
          <table className="w-full text-[12.5px]" data-testid="gbp-mapping-table">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#9CA3A0] border-b border-[#E2E8DF]">
                <th className="py-2 px-2 font-semibold">Outlet</th>
                <th className="py-2 px-2 font-semibold">Google location</th>
                <th className="py-2 px-2 font-semibold w-[60px] text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {outlets.map((o) => {
                const m = mappingByOutlet[o.id];
                const value = m ? `${m.google_user_id}::${m.google_location_name}` : '';
                return (
                  <tr key={o.id} className="border-b border-[#F1F4F0]" data-testid={`gbp-row-${o.id}`}>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-[#9CA3A0]" />
                        <div>
                          <div className="font-semibold text-[#1A2421]">{o.name}</div>
                          <div className="text-[11px] text-[#9CA3A0]">{o.city || o.legal_entity_name || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <Select
                        value={value || '__none__'}
                        onValueChange={(v) => v === '__none__' ? null : assignMapping(o, v)}
                        disabled={mappingBusy === o.id}
                      >
                        <SelectTrigger className="h-8 text-[12px]" data-testid={`gbp-select-${o.id}`}>
                          <SelectValue placeholder="— Not mapped —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__" disabled>— Select a Google location —</SelectItem>
                          {availableOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label} <span className="text-[10px] text-[#9CA3A0] ml-1">· {opt.account}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      {m ? (
                        <Button
                          onClick={() => assignMapping(o, '__unset__')}
                          disabled={mappingBusy === o.id}
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[11.5px] text-[#A33A3A] hover:bg-[#FDF4F4]"
                          data-testid={`gbp-unmap-${o.id}`}
                        >
                          {mappingBusy === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Unmap'}
                        </Button>
                      ) : <span className="text-[11px] text-[#9CA3A0]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────── 3. Reputation Dashboard ──────────────────── */
function ReputationDashboardCard({ summary, outlets, reviews, totalReviews, onSyncNow, syncing, hasMappings }) {
  const outletNames = useMemo(
    () => Object.fromEntries(outlets.map((o) => [o.id, o.name])),
    [outlets],
  );
  const k = summary?.kpis || { total_reviews: 0, avg_rating: 0, positive_pct: 0, reply_rate: 0 };
  const dist = summary?.distribution || { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 };
  const maxBar = Math.max(1, dist['5'], dist['4'], dist['3'], dist['2'], dist['1']);
  const lastSync = summary?.last_sync_at ? new Date(summary.last_sync_at) : null;

  return (
    <Card className="border-[#E2E8DF] shadow-sm" data-testid="gbp-reputation-card">
      <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <Star className="w-4 h-4 text-[#F5B100] fill-[#F5B100]" /> Reputation Dashboard
          </CardTitle>
          <div className="text-[10.5px] text-[#9CA3A0]">
            {lastSync
              ? `Last synced ${lastSync.toLocaleString()}`
              : 'Never synced yet'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 py-5 space-y-5">
        {!hasMappings && (
          <div className="p-3 rounded-md border border-[#E2E8DF] bg-[#F9F9F8] text-[12px] text-[#5C6B64]">
            Map at least one outlet to a Google location above, then click <b>Sync now</b> to pull reviews.
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gbp-kpis">
          <Kpi label="Total reviews" value={k.total_reviews} testid="gbp-kpi-total" />
          <Kpi label="Avg rating" value={k.avg_rating ? `${k.avg_rating} ★` : '—'} testid="gbp-kpi-avg" />
          <Kpi label="Positive (4-5★)" value={k.total_reviews ? `${k.positive_pct}%` : '—'} testid="gbp-kpi-positive" />
          <Kpi label="Reply rate" value={k.total_reviews ? `${k.reply_rate}%` : '—'} testid="gbp-kpi-replies" />
        </div>

        {k.total_reviews > 0 && (
          <>
            {/* Distribution */}
            <div data-testid="gbp-distribution">
              <div className="text-[11px] uppercase tracking-wider text-[#9CA3A0] mb-2">Rating distribution</div>
              <div className="space-y-1.5">
                {['5', '4', '3', '2', '1'].map((star) => {
                  const count = dist[star] || 0;
                  const pct = (count / maxBar) * 100;
                  const color = star === '5' || star === '4' ? 'var(--color-primary)'
                              : star === '3' ? '#D89B5B' : '#A33A3A';
                  return (
                    <div key={star} className="flex items-center gap-2 text-[12px]">
                      <div className="w-7 text-right text-[#5C6B64]">{star}★</div>
                      <div className="flex-1 h-4 bg-[#F4F7F4] rounded overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                      <div className="w-12 text-right text-[#1A2421] font-semibold">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per-outlet */}
            {summary.per_outlet?.length > 0 && (
              <div data-testid="gbp-per-outlet">
                <div className="text-[11px] uppercase tracking-wider text-[#9CA3A0] mb-2">Reputation by outlet</div>
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#9CA3A0] border-b border-[#E2E8DF]">
                      <th className="py-1.5 px-2 font-semibold">Outlet</th>
                      <th className="py-1.5 px-2 font-semibold text-right">Reviews</th>
                      <th className="py-1.5 px-2 font-semibold text-right">Avg ★</th>
                      <th className="py-1.5 px-2 font-semibold text-right">Reply rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.per_outlet.map((o) => (
                      <tr key={o.outlet_id} className="border-b border-[#F1F4F0]">
                        <td className="py-2 px-2 text-[#1A2421] font-medium">{outletNames[o.outlet_id] || o.outlet_id}</td>
                        <td className="py-2 px-2 text-right">{o.total}</td>
                        <td className="py-2 px-2 text-right font-semibold">{o.avg_rating} ★</td>
                        <td className="py-2 px-2 text-right">{o.reply_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent reviews */}
            <div data-testid="gbp-recent-reviews">
              <div className="text-[11px] uppercase tracking-wider text-[#9CA3A0] mb-2">
                Recent reviews · {totalReviews} total
              </div>
              <div className="space-y-2.5">
                {reviews.slice(0, 10).map((r) => (
                  <div
                    key={r.review_id}
                    className="p-3 rounded-md border border-[#E2E8DF] bg-white"
                    data-testid={`gbp-review-${r.review_id.split('/').pop()}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Stars rating={r.rating} />
                        <span className="text-[12px] font-semibold text-[#1A2421]">{r.reviewer_name}</span>
                        <span className="text-[10.5px] text-[#9CA3A0]">
                          {outletNames[r.outlet_id] ? `· ${outletNames[r.outlet_id]}` : ''}
                        </span>
                      </div>
                      <span className="text-[10.5px] text-[#9CA3A0]">
                        {r.create_time ? fmtDateIST(r.create_time) : ''}
                      </span>
                    </div>
                    {r.comment ? (
                      <p className="text-[12.5px] text-[#3A4541] leading-relaxed">{r.comment}</p>
                    ) : (
                      <p className="text-[12px] text-[#9CA3A0] italic">No written comment</p>
                    )}
                    {r.has_reply ? (
                      <div className="mt-2 ml-4 p-2 rounded bg-[#F4F7F4] border-l-2 border-[color:var(--color-primary)]">
                        <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--color-primary)] font-semibold mb-0.5 flex items-center gap-1">
                          <MessageSquare className="w-2.5 h-2.5" /> Owner reply
                        </div>
                        <div className="text-[12px] text-[#3A4541]">{r.reply_text}</div>
                      </div>
                    ) : (
                      <div className="mt-1.5 text-[10.5px] text-[#A33A3A] flex items-center gap-1">
                        <AlertCircle className="w-2.5 h-2.5" /> Awaiting owner reply
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {k.total_reviews === 0 && (
          <div className="p-4 rounded-md border border-dashed border-[#E2E8DF] text-center text-[12.5px] text-[#9CA3A0]" data-testid="gbp-empty-state">
            No reviews synced yet. {hasMappings ? 'Click "Sync now" above to pull reviews from Google.' : 'Map at least one outlet first.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="p-3 rounded-md border border-[#E2E8DF] bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3A0]">{label}</div>
      <div className="text-[20px] font-semibold text-[#1A2421] mt-0.5">{value}</div>
    </div>
  );
}


/* ───────────────── Locations error + Diagnostics panel ───────────────── */
const HINT_COPY = {
  missing_business_manage_scope: {
    title: 'Google did not grant the business.manage scope',
    body: 'The OAuth consent succeeded, but Google returned a token WITHOUT the "business.manage" scope. This means your OAuth Consent Screen in Google Cloud Console does not declare the scope, OR the app is in "Testing" mode and the scope hasn\'t been approved for this user. Add the scope at: APIs & Services → OAuth consent screen → Scopes → "Add or remove scopes" → search for business.manage. Then disconnect and reconnect.',
    cta: { label: 'Open OAuth consent screen', href: 'https://console.cloud.google.com/apis/credentials/consent' },
  },
  quota_zero: {
    title: 'Google Business Profile API quota = 0 (pre-approval)',
    body: 'Your OAuth project is authenticated but Google has not granted GBP API quota yet. Apply for access:',
    cta: { label: 'Apply for GBP API access', href: 'https://support.google.com/business/contact/api_default' },
  },
  api_not_enabled: {
    title: 'GBP API is not enabled on this Cloud project',
    body: 'Enable "My Business Account Management API" and "My Business Business Information API" in Google Cloud Console → APIs & Services → Library.',
    cta: { label: 'Open Cloud Console', href: 'https://console.cloud.google.com/apis/library' },
  },
  permission_denied: {
    title: 'Permission denied by Google',
    body: 'The authenticated Google account does not appear to own or manage a Google Business Profile, OR the OAuth project lacks the required permissions.',
    cta: null,
  },
  bad_scope_or_expired: {
    title: 'Token / scope problem',
    body: 'The access token is invalid, expired, or was granted without the business.manage scope. Reconnect the account.',
    cta: null,
  },
  not_found: {
    title: 'Resource not found',
    body: 'Google returned 404. The account or location may have been deleted or moved.',
    cta: null,
  },
  rate_limited: {
    title: 'Rate-limited by Google',
    body: 'Google temporarily throttled the request. Wait a minute and retry — or reduce sync frequency.',
    cta: null,
  },
  forbidden_unknown: {
    title: '403 from Google (reason not specified)',
    body: 'Google returned 403 without a recognized reason. See the raw response below.',
    cta: null,
  },
};

function LocationsErrorPanel({ error, diagnostics, diagBusy, diagOpen, setDiagOpen, onRunDiagnostics, onRetry }) {
  // Try to extract a structured Google error from the failed /locations call.
  let status = error?.status;
  let googleError = null;
  let hint = null;
  let api = null;
  if (error?.detail && typeof error.detail === 'object') {
    googleError = error.detail.google_error;
    hint = error.detail.hint;
    api = error.detail.api;
    if (!status) status = error.detail.status;
  } else if (error?.detail) {
    googleError = { message: String(error.detail) };
  }
  const copy = HINT_COPY[hint] || HINT_COPY.forbidden_unknown;

  return (
    <div className="p-3.5 rounded-md border border-[#FFD8A8] bg-[#FFF6E5] text-[12px] text-[#5C4528]" data-testid="gbp-locations-error">
      <div className="font-semibold mb-1 flex items-center gap-1.5">
        <AlertCircle className="w-3.5 h-3.5" />
        {error ? copy.title : 'No locations available yet'}
        {status ? <span className="ml-1.5 font-mono text-[10.5px] bg-white px-1.5 py-0.5 rounded border border-[#E6CFA8]">HTTP {status}</span> : null}
      </div>
      <div className="mb-2 leading-relaxed">
        {error ? copy.body : 'Connection is authenticated but Google returned zero locations. This is usually a quota or API-enablement issue.'}
      </div>
      {copy.cta && (
        <a
          href={copy.cta.href} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[color:var(--color-primary)] underline font-semibold mb-3"
        >
          {copy.cta.label} <ExternalLink className="w-3 h-3" />
        </a>
      )}

      {/* Raw Google response (always visible when present — the user asked
          for the actual API response, not a UI fallback). */}
      {googleError && (
        <div className="mt-3 bg-white border border-[#E6CFA8] rounded p-2.5" data-testid="gbp-raw-google-error">
          <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3A0] mb-1.5 flex items-center gap-1.5">
            <span>Raw Google response</span>
            {api ? <span className="font-mono text-[10px] text-[#5C6B64] normal-case">{api}</span> : null}
          </div>
          <pre className="text-[11px] text-[#1A2421] whitespace-pre-wrap break-all leading-snug max-h-[260px] overflow-auto font-mono">
{typeof googleError === 'string' ? googleError : JSON.stringify(googleError, null, 2)}
          </pre>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <Button
          onClick={onRunDiagnostics}
          disabled={diagBusy}
          size="sm"
          className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-8 text-[12px]"
          data-testid="gbp-run-diagnostics"
        >
          {diagBusy
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Probing…</>
            : <><Stethoscope className="w-3.5 h-3.5 mr-1.5" /> Run full diagnostics</>}
        </Button>
        <Button
          onClick={onRetry}
          variant="outline"
          size="sm"
          className="h-8 text-[12px] border-[#E6CFA8] text-[#5C4528]"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
        </Button>
      </div>

      {/* Diagnostics report */}
      {diagnostics && (
        <div className="mt-4 bg-white border border-[#E2E8DF] rounded" data-testid="gbp-diagnostics-report">
          <button
            type="button"
            onClick={() => setDiagOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-[#1A2421] hover:bg-[#F9F9F8] rounded-t"
          >
            <span className="flex items-center gap-2">
              <Stethoscope className="w-3.5 h-3.5 text-[color:var(--color-primary)]" />
              Diagnostic verdict: <span className="font-mono text-[11.5px] text-[#A33A3A]">{diagnostics.verdict}</span>
            </span>
            {diagOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {diagOpen && (
            <div className="px-3 pb-3 pt-1 space-y-2 border-t border-[#E2E8DF]">
              <div className="text-[11px] text-[#5C6B64] grid grid-cols-2 gap-x-3 gap-y-0.5">
                <div><span className="text-[#9CA3A0]">Email:</span> <span className="text-[#1A2421] font-medium">{diagnostics.stored_email}</span></div>
                <div><span className="text-[#9CA3A0]">Has business.manage scope:</span> <span className={diagnostics.has_business_manage_scope ? 'text-[color:var(--color-primary)] font-semibold' : 'text-[#A33A3A] font-semibold'}>{String(diagnostics.has_business_manage_scope)}</span></div>
                <div className="col-span-2"><span className="text-[#9CA3A0]">Granted scopes:</span> <span className="font-mono text-[10.5px] text-[#1A2421]">{(diagnostics.granted_scopes || []).join(' · ') || '(none)'}</span></div>
                <div><span className="text-[#9CA3A0]">Accounts:</span> {diagnostics.accounts_count ?? 0}</div>
                <div><span className="text-[#9CA3A0]">Locations in first account:</span> {diagnostics.locations_count_first_account ?? '—'}</div>
              </div>

              <div className="space-y-2 mt-2">
                {diagnostics.steps.map((s, i) => (
                  <div key={i} className={`border rounded p-2 ${s.ok ? 'border-[#D5E5DC] bg-[#F4F9F5]' : 'border-[#E6CFA8] bg-[#FFF8EC]'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-mono text-[10.5px] text-[#3A4541] truncate">{s.api}</div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-mono text-[10.5px] px-1.5 py-0.5 rounded ${s.ok ? 'bg-[#DCEFDE] text-[color:var(--color-primary)]' : 'bg-[#FCE3C0] text-[#5C4528]'}`}>
                          HTTP {s.status}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#5C6B64]">{s.hint}</span>
                      </div>
                    </div>
                    <pre className="text-[10.5px] font-mono text-[#1A2421] whitespace-pre-wrap break-all leading-snug max-h-[200px] overflow-auto bg-white rounded border border-[#E2E8DF] p-2">
{typeof s.body === 'string' ? s.body : JSON.stringify(s.body, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
